/**
 * Workflow Stepper Component
 * Compact horizontal progress stepper for project workflow stages. Each stage is
 * an equal flex-1 min-w-0 column (circle + wrapping label), and the connector is
 * an absolutely-positioned line between circle centers, so all stages fit within
 * narrow detail panels without overflowing or forcing a horizontal scroll.
 */

import { Check, Circle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { WorkflowStep } from '../types';

interface WorkflowStepperProps {
  steps: WorkflowStep[];
  onStepComplete?: (stepKey: string) => void;
  onStepUncomplete?: (stepKey: string) => void;
  onCompleteStep?: (stepKey: string) => void; // Alias for onStepComplete
  canEdit?: boolean;
  isCompletingStep?: boolean;
  className?: string;
}

export function WorkflowStepper({
  steps,
  onStepComplete,
  onStepUncomplete,
  onCompleteStep,
  canEdit: canEditProp,
  isCompletingStep,
  className,
}: WorkflowStepperProps) {
  // Enable editing if either callback is provided
  const canEdit = canEditProp ?? !!(onStepComplete || onCompleteStep);
  const handleComplete = onStepComplete || onCompleteStep;
  // Sort steps by sortOrder
  const sortedSteps = [...steps].sort((a, b) => a.sortOrder - b.sortOrder);

  // Find the first uncompleted step (current step)
  const currentIndex = sortedSteps.findIndex((s) => !s.completed);

  const handleStepClick = (step: WorkflowStep, index: number) => {
    if (!canEdit || isCompletingStep) return;

    if (step.completed) {
      // Allow uncompleting only if it's the last completed step
      const isLastCompleted = index === currentIndex - 1 || (currentIndex === -1 && index === sortedSteps.length - 1);
      if (isLastCompleted && onStepUncomplete) {
        onStepUncomplete(step.key);
      }
    } else {
      // Allow completing only the current step (first uncompleted)
      if (index === currentIndex && handleComplete) {
        handleComplete(step.key);
      }
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-CA', {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  };

  return (
    <div className={cn('w-full', className)}>
      <div className="flex w-full items-start">
        {sortedSteps.map((step, index) => {
          const isCompleted = step.completed;
          const isCurrent = index === currentIndex;
          const isPending = !isCompleted && !isCurrent;
          const isLast = index === sortedSteps.length - 1;
          const isClickable = canEdit && (
            (isCompleted && (index === currentIndex - 1 || (currentIndex === -1 && index === sortedSteps.length - 1))) ||
            isCurrent
          );
          const connectorDone = index < currentIndex || (currentIndex === -1 && isCompleted);

          return (
            <div key={step.id} className="relative flex min-w-0 flex-1 flex-col items-center">
              {/* Connector line: absolute, runs from this circle's center to the next circle's center */}
              {!isLast && (
                <div
                  className={cn(
                    'absolute top-4 left-1/2 h-0.5 w-full -translate-y-1/2',
                    connectorDone ? 'bg-green-500' : 'bg-gray-200'
                  )}
                />
              )}

              {/* Step indicator (sits above the connector) */}
              <button
                type="button"
                disabled={!isClickable}
                onClick={() => handleStepClick(step, index)}
                className={cn(
                  'relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 transition-all duration-200',
                  isCompleted && 'bg-green-500 border-green-500 text-white',
                  isCurrent && 'bg-blue-500 border-blue-500 text-white',
                  isPending && 'bg-gray-100 border-gray-300 text-gray-400',
                  isClickable && 'cursor-pointer hover:scale-110 hover:ring-2 hover:ring-offset-2',
                  isClickable && isCurrent && 'hover:ring-blue-300',
                  isClickable && isCompleted && 'hover:ring-green-300',
                  !isClickable && 'cursor-default'
                )}
                title={isClickable ? (isCompleted ? 'Click to undo this step' : 'Click to complete this step') : undefined}
              >
                {isCompleted ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <span className="text-[11px] font-semibold">{index + 1}</span>
                )}
              </button>

              {/* Step label wraps within its column */}
              <div className="mt-1.5 w-full px-1 text-center">
                <p
                  className={cn(
                    'break-words text-[10px] font-medium leading-tight',
                    isCompleted && 'text-green-700',
                    isCurrent && 'text-blue-700',
                    isPending && 'text-gray-500'
                  )}
                >
                  {step.displayName}
                </p>
                {step.completedAt && (
                  <p className="mt-0.5 text-[10px] text-gray-400">
                    {formatDate(step.completedAt)}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// Compact Variant for List Cards
// =============================================================================

interface WorkflowStepperCompactProps {
  steps: WorkflowStep[];
  className?: string;
}

export function WorkflowStepperCompact({ steps, className }: WorkflowStepperCompactProps) {
  // Sort steps by sortOrder
  const sortedSteps = [...steps].sort((a, b) => a.sortOrder - b.sortOrder);

  const completedSteps = sortedSteps.filter((s) => s.completed);
  const completedCount = completedSteps.length;
  const totalSteps = sortedSteps.length;
  const lastCompletedStep = completedSteps.length > 0 ? completedSteps[completedSteps.length - 1] : null;
  const currentStep = sortedSteps.find((s) => !s.completed);

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {/* Mini dots */}
      <div className="flex items-center gap-1">
        {sortedSteps.map((step) => (
          <Circle
            key={step.id}
            className={cn(
              'h-2 w-2',
              step.completed && 'fill-green-500 text-green-500',
              !step.completed && step === currentStep && 'fill-blue-500 text-blue-500',
              !step.completed && step !== currentStep && 'fill-gray-300 text-gray-300'
            )}
          />
        ))}
      </div>

      {/* Last completed step label or waiting status */}
      <span className="text-xs text-muted-foreground truncate">
        {completedCount === totalSteps ? (
          'Complete'
        ) : lastCompletedStep ? (
          lastCompletedStep.displayName
        ) : (
          `${completedCount}/${totalSteps}`
        )}
      </span>
    </div>
  );
}
